const MODE_LABELS = {
    homeVisit: '방문 돌봄',
    sitterHome: '시터 집',
    petHotel: '펫호텔',
    daycare: '데이케어',
    trustedPerson: '가족·지인',
    transport: '이동 돌봄',
    grooming: '미용',
};
const DEFAULT_TASKS = {
    homeVisit: [
        { phase: 'before', label: '출입 방법과 방문 시각 다시 확인' },
        { phase: 'handoff', label: '돌봄표와 연락 순서 함께 확인' },
        { phase: 'during', label: '식사·배변·활동 체크인 남기기' },
        { phase: 'return', label: '마지막 상태와 집 상태 확인' },
    ],
    sitterHome: [
        { phase: 'before', label: '함께 지내는 동물과 분리 방법 확인' },
        { phase: 'handoff', label: '돌봄표와 소지품 함께 확인' },
        { phase: 'during', label: '약속한 시각에 체크인 남기기' },
        { phase: 'return', label: '귀가 인수증 함께 확인' },
    ],
    petHotel: [
        { phase: 'before', label: '야간 인력과 예외 연락 순서 확인' },
        { phase: 'handoff', label: '돌봄표와 보관 물품 함께 확인' },
        { phase: 'during', label: '식사·배변·활동 체크인 남기기' },
        { phase: 'return', label: '귀가 상태와 소지품 함께 확인' },
    ],
    daycare: [
        { phase: 'before', label: '합사·휴식·연락 기준 확인' },
        { phase: 'handoff', label: '당일 돌봄표 함께 확인' },
        { phase: 'during', label: '활동과 휴식 체크인 남기기' },
        { phase: 'return', label: '당일 달라진 사실 확인' },
    ],
    trustedPerson: [
        { phase: 'before', label: '집·산책·급여 순서 함께 보기' },
        { phase: 'handoff', label: '돌봄표와 연락 순서 함께 확인' },
        { phase: 'during', label: '완료한 돌봄 표시하기' },
        { phase: 'return', label: '남은 돌봄과 달라진 사실 확인' },
    ],
    transport: [
        { phase: 'before', label: '출발·도착 시각과 이동장 확인' },
        { phase: 'handoff', label: '연락 순서와 목적지 확인' },
        { phase: 'during', label: '출발·도착 사실 남기기' },
        { phase: 'return', label: '인계받은 사람과 시각 확인' },
    ],
    grooming: [
        { phase: 'before', label: '보호자 요청과 도착 상태 함께 확인' },
        { phase: 'handoff', label: '중지·연락 기준 함께 확인' },
        { phase: 'during', label: '작업 변경과 관찰 사실 남기기' },
        { phase: 'return', label: '완료 사실과 집에서 볼 내용 확인' },
    ],
};
function text(value, max) {
    return String(value ?? '').normalize('NFC').replace(/\s+/gu, ' ').trim().slice(0, max);
}
function id(prefix) {
    return `${prefix}-${crypto.randomUUID()}`;
}
function requireOpen(episode) {
    if (episode.status === 'returned')
        throw new Error('returned_episode_is_read_only');
}
export function validateCarePassport(input, now = Date.now()) {
    const value = {
        id: text(input.id, 120) || id('care-passport'),
        petId: text(input.petId, 120),
        meal: text(input.meal, 240),
        activity: text(input.activity, 240),
        toilet: text(input.toilet, 180),
        aloneMinutes: Math.max(0, Math.min(1_440, Number(input.aloneMinutes) || 0)),
        comfort: text(input.comfort, 240),
        caution: text(input.caution, 300),
        mustNotDo: text(input.mustNotDo, 300),
        medicationHandoff: text(input.medicationHandoff, 300),
        emergencyContact: text(input.emergencyContact, 120),
        vetContact: text(input.vetContact, 120),
        reviewedAt: now,
    };
    const errors = [];
    if (!value.petId)
        errors.push('pet_required');
    if (![value.meal, value.activity, value.comfort, value.caution].some(Boolean))
        errors.push('one_care_fact_required');
    return { value, errors };
}
export function createCareEpisode(input, now = Date.now()) {
    if (!Number.isFinite(input.startsAt) || !Number.isFinite(input.endsAt) || input.startsAt >= input.endsAt)
        throw new Error('care_date_order_invalid');
    const petId = text(input.petId, 120);
    if (!petId)
        throw new Error('pet_required');
    const mode = input.mode;
    if (!(mode in MODE_LABELS))
        throw new Error('care_mode_invalid');
    return {
        id: id('care-episode'),
        petId,
        title: text(input.title, 80) || `${MODE_LABELS[mode]} 일정`,
        mode,
        startsAt: input.startsAt,
        endsAt: input.endsAt,
        status: 'draft',
        modeAnswers: {
            environmentSensitive: Boolean(input.modeAnswers?.environmentSensitive),
            needsOvernightCare: Boolean(input.modeAnswers?.needsOvernightCare),
            avoidOtherPets: Boolean(input.modeAnswers?.avoidOtherPets),
            frequentCare: Boolean(input.modeAnswers?.frequentCare),
        },
        agreement: { providerLabel: '', handoffAt: input.startsAt, returnAt: input.endsAt, checkinRhythm: '', emergencyAction: '', supplies: '' },
        tasks: DEFAULT_TASKS[mode].map((task) => ({ ...task, id: id('care-task'), done: false })),
        checkins: [],
        exceptions: [],
        ...(mode === 'grooming' ? { grooming: emptyGroomingDetail() } : {}),
        returnReceipt: null,
    };
}
export function confirmCareAgreement(episode, input, now = Date.now()) {
    requireOpen(episode);
    const agreement = {
        providerLabel: text(input.providerLabel, 80),
        handoffAt: Number(input.handoffAt) || episode.startsAt,
        returnAt: Number(input.returnAt) || episode.endsAt,
        checkinRhythm: text(input.checkinRhythm, 160),
        emergencyAction: text(input.emergencyAction, 300),
        supplies: text(input.supplies, 240),
        confirmedAt: now,
    };
    if (!agreement.providerLabel || !agreement.checkinRhythm || !agreement.emergencyAction)
        throw new Error('care_agreement_incomplete');
    return { ...episode, agreement, status: 'planned', updatedAt: now };
}
export function startCareEpisode(episode, now = Date.now()) {
    requireOpen(episode);
    if (!episode.agreement.confirmedAt)
        throw new Error('care_agreement_required');
    return { ...episode, status: 'active', startedAt: now, updatedAt: now };
}
export function toggleCareTask(episode, taskId, done, actorLabel, now = Date.now()) {
    requireOpen(episode);
    if (!episode.tasks.some((task) => task.id === taskId))
        throw new Error('care_task_not_found');
    const completedBy = text(actorLabel, 20);
    return {
        ...episode,
        tasks: episode.tasks.map((task) => task.id === taskId
            ? done
                ? { ...task, done: true, completedAt: now, ...(completedBy ? { completedBy } : {}) }
                : { ...task, done: false, completedAt: undefined, completedBy: undefined }
            : task),
        updatedAt: now,
    };
}
export function addCareCheckin(episode, input, now = Date.now()) {
    requireOpen(episode);
    if (episode.status !== 'active')
        throw new Error('care_episode_not_active');
    const checkin = {
        id: id('care-checkin'),
        at: now,
        ...(text(input.actorLabel, 20) ? { actorLabel: text(input.actorLabel, 20) } : {}),
        mood: input.mood === 'watching' || input.mood === 'changed' ? input.mood : 'settled',
        meal: text(input.meal, 160) || undefined,
        toilet: text(input.toilet, 160) || undefined,
        activity: text(input.activity, 200) || undefined,
        note: text(input.note, 500) || undefined,
    };
    if (![checkin.meal, checkin.toilet, checkin.activity, checkin.note].some(Boolean))
        throw new Error('care_checkin_empty');
    return { ...episode, checkins: [checkin, ...episode.checkins], updatedAt: now };
}
export function addCareException(episode, input, now = Date.now()) {
    requireOpen(episode);
    if (episode.status !== 'active')
        throw new Error('care_episode_not_active');
    const exception = {
        id: id('care-exception'),
        at: now,
        ...(text(input.actorLabel, 20) ? { actorLabel: text(input.actorLabel, 20) } : {}),
        observedFact: text(input.observedFact, 300),
        actionTaken: text(input.actionTaken, 300),
        contactedAt: input.contactedAt ? Number(input.contactedAt) : null,
        resolvedAt: null,
    };
    if (!exception.observedFact || !exception.actionTaken)
        throw new Error('care_exception_incomplete');
    return { ...episode, exceptions: [exception, ...episode.exceptions], updatedAt: now };
}
export function resolveCareException(episode, exceptionId, now = Date.now()) {
    requireOpen(episode);
    if (!episode.exceptions.some((item) => item.id === exceptionId))
        throw new Error('care_exception_not_found');
    return { ...episode, exceptions: episode.exceptions.map((item) => item.id === exceptionId ? { ...item, resolvedAt: now } : item), updatedAt: now };
}
export function updateGroomingHandoff(episode, input, now = Date.now()) {
    requireOpen(episode);
    if (episode.mode !== 'grooming')
        throw new Error('grooming_episode_required');
    const current = episode.grooming ?? emptyGroomingDetail();
    const grooming = {
        guardianRequest: text(input.guardianRequest ?? current.guardianRequest, 500),
        arrivalObservation: text(input.arrivalObservation ?? current.arrivalObservation, 500),
        stopRule: text(input.stopRule ?? current.stopRule, 300),
        serviceChanges: text(input.serviceChanges ?? current.serviceChanges, 500),
        completionFacts: text(input.completionFacts ?? current.completionFacts, 700),
        homeWatch: text(input.homeWatch ?? current.homeWatch, 500),
    };
    if (!grooming.guardianRequest || !grooming.arrivalObservation || !grooming.stopRule)
        throw new Error('grooming_arrival_incomplete');
    return { ...episode, grooming, updatedAt: now };
}
export function closeCareEpisode(episode, input, now = Date.now()) {
    requireOpen(episode);
    if (episode.status !== 'active')
        throw new Error('care_episode_not_active');
    const receipt = {
        returnedAt: now,
        ...(text(input.actorLabel, 20) ? { actorLabel: text(input.actorLabel, 20) } : {}),
        meal: text(input.meal, 160),
        toilet: text(input.toilet, 160),
        conditionFact: text(input.conditionFact, 300),
        supplies: text(input.supplies, 200),
        followup: text(input.followup, 300),
        wouldReuse: input.wouldReuse === true,
    };
    if (!receipt.conditionFact)
        throw new Error('return_fact_required');
    if (episode.mode === 'grooming' && !(episode.grooming?.completionFacts && episode.grooming.homeWatch))
        throw new Error('grooming_completion_incomplete');
    return { ...episode, status: 'returned', returnReceipt: receipt, updatedAt: now };
}
export function cloneCareEpisode(episode, now = Date.now()) {
    return {
        ...structuredClone(episode),
        id: id('care-episode'),
        title: `${episode.title} 다시 준비`,
        startsAt: 0,
        endsAt: 0,
        status: 'draft',
        agreement: { ...episode.agreement, providerLabel: '', handoffAt: 0, returnAt: 0, confirmedAt: null },
        tasks: episode.tasks.map((task) => ({ ...task, id: id('care-task'), done: false, completedAt: undefined, completedBy: undefined })),
        checkins: [],
        exceptions: [],
        returnReceipt: null,
        startedAt: null,
    };
}
export function carePreparedness(passport, episode) {
    const checks = [
        { id: 'passport', label: '돌봄표', done: Boolean(passport) },
        { id: 'schedule', label: '일정', done: Boolean(episode?.startsAt && episode?.endsAt) },
        { id: 'agreement', label: '인계 합의', done: Boolean(episode?.agreement.confirmedAt) },
        { id: 'emergency', label: '연락 순서', done: Boolean(episode?.agreement.emergencyAction) },
    ];
    return { checks, complete: checks.filter((item) => item.done).length, total: checks.length };
}
export function careModeLabel(mode) {
    return MODE_LABELS[mode];
}
/**
 * Applies only fields a care provider is allowed to author. The server-supplied
 * author and acceptedAt values replace labels and clocks inside the encrypted
 * packet, because possession of the invite token is not authority to rewrite
 * the guardian's schedule, agreement, passport, or stop rules.
 */
export function mergeProviderCareEpisodeUpdate(current, incoming, actorLabel, acceptedAt) {
    if (current.status === 'returned')
        return current;
    if (!incoming || incoming.id !== current.id || incoming.petId !== current.petId || incoming.mode !== current.mode)
        throw new Error('care_provider_packet_mismatch');
    if (!Array.isArray(incoming.tasks) || !Array.isArray(incoming.checkins) || !Array.isArray(incoming.exceptions))
        throw new Error('care_provider_packet_invalid');
    if (incoming.tasks.length !== current.tasks.length || incoming.tasks.length > 50 || incoming.checkins.length > 200 || incoming.exceptions.length > 100)
        throw new Error('care_provider_packet_invalid');
    const author = text(actorLabel, 20) || '돌봄자';
    const at = Number.isFinite(acceptedAt) && acceptedAt > 0 ? acceptedAt : Date.now();
    const incomingTasks = new Map(incoming.tasks.map((task) => [text(task?.id, 120), task]));
    const tasks = current.tasks.map((task) => {
        const candidate = incomingTasks.get(task.id);
        if (!candidate || candidate.phase !== task.phase || text(candidate.label, 300) !== task.label)
            throw new Error('care_provider_task_mismatch');
        if (Boolean(candidate.done) === task.done)
            return task;
        return candidate.done
            ? { ...task, done: true, completedBy: author, completedAt: at }
            : { ...task, done: false, completedBy: undefined, completedAt: undefined };
    });
    const currentCheckinIds = new Set(current.checkins.map((item) => item.id));
    const addedCheckins = incoming.checkins.filter((item) => !currentCheckinIds.has(text(item?.id, 120)));
    if (addedCheckins.length > 50)
        throw new Error('care_provider_packet_too_many_changes');
    const checkins = [
        ...addedCheckins.map((item) => {
            const next = {
                id: text(item?.id, 120),
                at,
                actorLabel: author,
                mood: item?.mood === 'watching' || item?.mood === 'changed' ? item.mood : 'settled',
                meal: text(item?.meal, 160) || undefined,
                toilet: text(item?.toilet, 160) || undefined,
                activity: text(item?.activity, 200) || undefined,
                note: text(item?.note, 500) || undefined,
            };
            if (!next.id || ![next.meal, next.toilet, next.activity, next.note].some(Boolean))
                throw new Error('care_provider_checkin_invalid');
            return next;
        }),
        ...current.checkins,
    ];
    const currentExceptionIds = new Set(current.exceptions.map((item) => item.id));
    const addedExceptions = incoming.exceptions.filter((item) => !currentExceptionIds.has(text(item?.id, 120)));
    if (addedExceptions.length > 30)
        throw new Error('care_provider_packet_too_many_changes');
    const exceptions = [
        ...addedExceptions.map((item) => {
            const next = {
                id: text(item?.id, 120),
                at,
                actorLabel: author,
                observedFact: text(item?.observedFact, 300),
                actionTaken: text(item?.actionTaken, 300),
                contactedAt: Number.isFinite(Number(item?.contactedAt)) ? Number(item.contactedAt) : null,
                resolvedAt: null,
            };
            if (!next.id || !next.observedFact || !next.actionTaken)
                throw new Error('care_provider_exception_invalid');
            return next;
        }),
        ...current.exceptions,
    ];
    const grooming = current.grooming && incoming.grooming ? {
        ...current.grooming,
        serviceChanges: text(incoming.grooming.serviceChanges, 500),
        completionFacts: text(incoming.grooming.completionFacts, 700),
        homeWatch: text(incoming.grooming.homeWatch, 500),
    } : current.grooming;
    let status = current.status;
    let startedAt = current.startedAt;
    let returnReceipt = current.returnReceipt;
    if (current.status === 'planned' && incoming.status === 'active') {
        status = 'active';
        startedAt = at;
    }
    else if (current.status === 'active' && incoming.status === 'returned') {
        const receipt = incoming.returnReceipt;
        const conditionFact = text(receipt?.conditionFact, 300);
        if (!receipt || !conditionFact)
            throw new Error('care_provider_return_invalid');
        if (current.mode === 'grooming' && !(grooming?.completionFacts && grooming.homeWatch))
            throw new Error('grooming_completion_incomplete');
        status = 'returned';
        returnReceipt = {
            returnedAt: at,
            actorLabel: author,
            meal: text(receipt.meal, 160),
            toilet: text(receipt.toilet, 160),
            conditionFact,
            supplies: text(receipt.supplies, 200),
            followup: text(receipt.followup, 300),
            wouldReuse: receipt.wouldReuse === true,
        };
    }
    return { ...current, tasks, checkins, exceptions, ...(grooming ? { grooming } : {}), status, startedAt, returnReceipt, updatedAt: at };
}
export function buildCareEpisodePacket(petName, passport, episode, productName = '펫리듬') {
    const lines = [
        `[${text(productName, 20) || '펫리듬'}] ${text(petName, 20)} · ${careModeLabel(episode.mode)} 인계`,
        `${new Date(episode.startsAt).toLocaleString('ko-KR')} ~ ${new Date(episode.endsAt).toLocaleString('ko-KR')}`,
        '',
        '생활 돌봄표',
        `- 식사: ${passport?.meal || '미작성'}`,
        `- 활동: ${passport?.activity || '미작성'}`,
        `- 배변: ${passport?.toilet || '미작성'}`,
        `- 안정되는 방법: ${passport?.comfort || '미작성'}`,
        `- 주의할 사실: ${passport?.caution || '미작성'}`,
        `- 하지 않기로 한 것: ${passport?.mustNotDo || '미작성'}`,
        `- 투약 인계: ${passport?.medicationHandoff || '없음 또는 미작성'}`,
        '',
        '이번 돌봄 합의',
        `- 돌봄자·장소: ${episode.agreement.providerLabel || '미작성'}`,
        `- 체크인 약속: ${episode.agreement.checkinRhythm || '미작성'}`,
        `- 예외 연락 순서: ${episode.agreement.emergencyAction || '미작성'}`,
        `- 소지품: ${episode.agreement.supplies || '미작성'}`,
    ];
    if (episode.mode === 'grooming' && episode.grooming)
        lines.push('', '미용 인계', `- 보호자 요청: ${episode.grooming.guardianRequest || '미작성'}`, `- 도착 때 함께 본 사실: ${episode.grooming.arrivalObservation || '미작성'}`, `- 사람이 정한 중지·연락 기준: ${episode.grooming.stopRule || '미작성'}`, `- 작업 중 바뀐 일: ${episode.grooming.serviceChanges || '없음 또는 미작성'}`, `- 완료 사실: ${episode.grooming.completionFacts || '미작성'}`, `- 집에서 볼 내용: ${episode.grooming.homeWatch || '미작성'}`);
    lines.push('', '이 문서는 사용자가 적고 확인한 사실입니다. 진단·안전 보증·전자서명이 아닙니다.');
    return lines.join('\n');
}
function emptyGroomingDetail() {
    return { guardianRequest: '', arrivalObservation: '', stopRule: '', serviceChanges: '', completionFacts: '', homeWatch: '' };
}
